import { useEffect, useState } from 'react';

import type { NewsletterDetail } from '../api/types';

import { fetchNewsletter } from '../api/infoHubApi';
import { extractRenderableHtml } from '../utils/contentHtml';

export type NewsletterState = 'loading' | 'ready' | 'notfound' | 'error';

export interface UseNewsletterResult {
  newsletter: NewsletterDetail | null;
  /** Render-ready HTML, wrappers stripped. Empty when the newsletter has no content. */
  html: string;
  state: NewsletterState;
}

/**
 * Loads a single newsletter by slug or GUID and prepares its HTML for rendering.
 *
 * `notfound` and `error` stay distinct: a missing or inactive newsletter is an expected
 * outcome the reader should see a friendly message for, not a failure worth logging.
 */
export const useNewsletter = (idOrSlug: string): UseNewsletterResult => {
  const [newsletter, setNewsletter] = useState<NewsletterDetail | null>(null);
  const [html, setHtml] = useState('');
  const [state, setState] = useState<NewsletterState>('loading');

  useEffect(() => {
    let cancelled = false;

    setState('loading');
    setNewsletter(null);
    setHtml('');

    const key = (idOrSlug || '').trim();
    if (!key) {
      setState('notfound');
      return;
    }

    const load = async (): Promise<void> => {
      try {
        const result = await fetchNewsletter(key);
        if (cancelled) {
          return;
        }

        if (!result) {
          setState('notfound');
          return;
        }

        setNewsletter(result);
        setHtml(extractRenderableHtml(result.contentHtml));
        setState('ready');
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error('Error loading newsletter:', error);
        setState('error');
      }
    };

    load();
    return (): void => {
      cancelled = true;
    };
  }, [idOrSlug]);

  return { newsletter, html, state };
};
